import { useEffect, useRef } from "react";

/**
 * Prevents the device screen from sleeping during a video meeting.
 *
 * Uses the Screen Wake Lock API — graceful no-op when unsupported.
 * Re-acquires the lock on foreground return since browsers release it
 * when the tab is backgrounded.
 */
export function useScreenWakeLock(isEnabled: boolean): void {
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    if (!isEnabled || !("wakeLock" in navigator)) return;

    const request = async () => {
      try {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
        wakeLockRef.current.addEventListener("release", () => {
          wakeLockRef.current = null;
        });
      } catch {
        // Permission denied or not supported — non-fatal
      }
    };

    request();

    // Browsers release the wake lock when the tab is backgrounded.
    // Re-acquire on foreground return.
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && !wakeLockRef.current) {
        request();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      wakeLockRef.current?.release().catch(() => {});
      wakeLockRef.current = null;
    };
  }, [isEnabled]);
}
